import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

import { ContextService } from 'src/core/services/context.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { ControllerProps } from 'src/router';
import { ApiKeyListView } from './api-key-list.view';
import { ApiKeyListDataService } from './api-key-list.data';

interface State {
	mode: ControlMode;
	tokens: { id: number; token: string; }[];
}

@observer
export class ApiKeyListController extends React.Component<ControllerProps<any>, State> {
	public state: State = {
		mode: 'display',
		tokens: [],
	};

	@resolve
	private toast: ToastNotifications;

	@observable
	private dataService: ApiKeyListDataService;

	@resolve
	private contextService: ContextService;

	public componentDidMount() {
		this.loadData();
	}

	public render() {
		if (this.dataService == null || this.dataService.items == null) {
			return null;
		}

		const { mode, tokens } = this.state;

		return (
			<ApiKeyListView
				model={this.dataService}
				mode={mode}
				onModeChange={this.handleModeChange}
				createNewItem={this.createNewItem}
				save={this.save}
				cancel={this.cancel}
				setBusy={this.props.setBusy}
				tokens={tokens}
				getToken={this.getToken}
			/>
		);
	}

	private async loadData(): Promise<void> {
		try {
			this.props.setBusy(true);
			this.dataService = ApiKeyListDataService.make(this.contextService);

			this.dataService.addSorting('id', 'asc');
			this.dataService.pageSize = 25;

			await this.dataService.load(query => query);
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		} finally {
			this.props.setBusy(false);
		}
	}

	private handleModeChange = (mode: ControlMode): void => {
		this.setState({ mode: mode });
	};

	private createNewItem = async (): Promise<ApiKeyItem> => {
		const item = await this.dataService.add();

		return item;
	};

	private save = async (): Promise<void> => {
		try {
			this.props.setBusy(true);
			await this.dataService.save();
			this.toast.showDataSaved();
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		} finally {
			this.props.setBusy(false);
		}
	};

	private cancel = async (): Promise<void> => {
		this.props.setBusy(true);
		this.handleModeChange('display');
		this.props.setBusy(false);
	};

	private getToken = (id: number) => async () => {
		const ctx = await this.contextService.current();
		const token = await ctx.GetToken(id);
		this.setState({ tokens: [...this.state.tokens, { id, token }] });
	}
}



// WEBPACK FOOTER //
// ./src/app/general/api-keys/api-key-list.controller.tsx